import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download } from 'lucide-react';
import type { FeedbackItem, FeedbackResult, FeedbackView } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, Mono, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus, StatusBadge } from '@/components/ui/status-badge';
import { api } from '@/lib/api';
import { IfsArtifactsView } from '@/components/ifs-artifacts-view';
import { useSession } from '@/lib/session';

/**
 * CREATE_IFS result (ADR-030): the IFS draft as a GBA entry plan. Grouped by screen/section in
 * entry order so the Integration Architect can copy each value into GBA (entry stays manual).
 * Every value shows its epistemic status and evidence; gaps are explicit, never filled.
 */
interface Ref {
  kind: string;
  id: string;
  version?: string;
  url?: string;
  locator?: { anchor?: string; section?: string };
}
interface PlanValue {
  value: string | null;
  status: string;
  evidence: Ref[];
  rationale?: string;
}
export interface IfsDraftShape {
  aad: { title: string; issueKey: string | null; source: Ref };
  gbaPlan: { order: number; screen: string; section: string; field: string; value: PlanValue }[];
  dataModel: {
    part: string;
    action: string;
    field: string;
    type: string;
    occurrence: string;
    maxLength: string;
    gdpr: boolean;
    rules: string;
    description: string;
  }[];
  findings: {
    rule: string;
    severity: 'High' | 'Med' | 'Low';
    message: string;
    standard: Ref | null;
    standardTitle: string | null;
    origin?: 'check' | 'review';
    verified?: boolean;
    recommendation?: string;
  }[];
  openQuestions: string[];
  appliedStandards: PlanValue[];
  /** IFS model v2 (CREATE_IFS v4). */
  artifacts?: Parameters<typeof IfsArtifactsView>[0]['artifacts'];
  operationModels?: Parameters<typeof IfsArtifactsView>[0]['models'];
  summary: {
    entries: number;
    verified: number;
    inferred: number;
    needsInput: number;
    completeness?: number;
    findings: { high: number; med: number; low: number };
  };
}

export function isIfsDraft(v: unknown): v is IfsDraftShape {
  return !!v && typeof v === 'object' && Array.isArray((v as IfsDraftShape).gbaPlan);
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      aria-label="Copy value"
      className="px-2 py-1"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? (
        <Check aria-hidden="true" className="size-3.5" />
      ) : (
        <Copy aria-hidden="true" className="size-3.5" />
      )}
      <span className="sr-only">{done ? 'Copied' : 'Copy'}</span>
    </Button>
  );
}

const refLabel = (r: Ref) =>
  `${r.id}${r.version ? ` v${r.version}` : ''}${r.locator?.anchor ? ` · ${r.locator.anchor}` : ''}`;

function toMarkdown(d: IfsDraftShape): string {
  const out: string[] = [
    `# IFS draft — ${d.aad.title}`,
    '',
    '> Draft prepared by Agentic OS from the AAD. Review before entering it in GBA; nothing was written anywhere.',
    '',
  ];
  let section = '';
  for (const e of d.gbaPlan) {
    const s = `${e.screen} · ${e.section}`;
    if (s !== section) {
      out.push('', `## ${s}`, '');
      section = s;
    }
    const v =
      e.value.value ?? `_${e.value.status}${e.value.rationale ? `: ${e.value.rationale}` : ''}_`;
    out.push(`**${e.field}** (${e.value.status})`, '', v, '');
  }
  if (d.dataModel.length) {
    out.push(
      '',
      '## Data model',
      '',
      '| Part | Action | Field | Type | Occurrence | Max length | GDPR | Rules | Description |',
      '|---|---|---|---|---|---|---|---|---|',
    );
    for (const r of d.dataModel)
      out.push(
        `| ${r.part} | ${r.action} | ${r.field} | ${r.type} | ${r.occurrence} | ${r.maxLength} | ${r.gdpr ? 'X' : ''} | ${r.rules} | ${r.description} |`,
      );
  }
  if (d.findings.length) {
    out.push('', '## Findings', '');
    for (const f of d.findings)
      out.push(
        `- **${f.severity}** ${f.message}${f.standardTitle ? ` _(standard: ${f.standardTitle})_` : ''}${f.recommendation ? ` — ${f.recommendation}` : ''}${f.origin === 'review' && !f.verified ? ' _(unverified)_' : ''}`,
      );
  }
  if (d.openQuestions.length) {
    out.push('', '## Open questions', '');
    for (const q of d.openQuestions) out.push(`- ${q}`);
  }
  return `${out.join('\n')}\n`;
}

type Submit = (item: Omit<FeedbackItem, 'type'> & { type?: string }) => void;

/**
 * Reviewer decision on one draft element (ADR-031): accept or correct it (it becomes a knowledge
 * candidate, validated later in Knowledge → Review) or reject it. Decided once.
 */
function ReviewCell({
  path,
  label,
  value,
  type,
  done,
  canReview,
  busy,
  submit,
}: {
  path: string;
  label: string;
  value: string | null;
  type: string;
  done: FeedbackView | undefined;
  canReview: boolean;
  busy: boolean;
  submit: Submit;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value ?? '');
  if (done)
    return (
      <span className="block text-xs text-muted">
        <DomainStatus value={done.decision === 'reject' ? 'rejected' : 'approved'} />{' '}
        {done.decision === 'edit' ? 'corrected' : done.decision}
        {done.knowledgeStatus && <> · knowledge {done.knowledgeStatus.toLowerCase()}</>}
      </span>
    );
  if (!canReview) return null;
  if (editing)
    return (
      <span className="block space-y-1">
        <textarea
          aria-label={`Corrected value for ${label}`}
          className="w-full rounded-md border border-border bg-surface p-2 text-sm"
          rows={3}
          maxLength={4000}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <span className="flex gap-1">
          <Button
            variant="primary"
            disabled={busy || !text.trim()}
            onClick={() => submit({ path, label, decision: 'edit', value: text, type })}
          >
            Save correction
          </Button>
          <Button onClick={() => setEditing(false)}>Cancel</Button>
        </span>
      </span>
    );
  return (
    <span className="flex flex-wrap gap-1">
      <Button
        className="px-2 py-1 text-xs"
        disabled={busy || value === null}
        onClick={() => submit({ path, label, decision: 'accept', value: value ?? '', type })}
      >
        Accept
      </Button>
      <Button className="px-2 py-1 text-xs" disabled={busy} onClick={() => setEditing(true)}>
        Correct
      </Button>
      <Button
        variant="danger"
        className="px-2 py-1 text-xs"
        disabled={busy}
        onClick={() => submit({ path, label, decision: 'reject' })}
      >
        Reject
      </Button>
    </span>
  );
}

export function IfsDraftView({
  draft,
  executionId,
}: {
  draft: IfsDraftShape;
  /** When set, reviewers can accept, correct or reject each element (feedback loop). */
  executionId?: string;
}) {
  const { can } = useSession();
  const qc = useQueryClient();
  const canReview = !!executionId && can('knowledge.review');
  const feedback = useQuery({
    queryKey: ['feedback', executionId],
    queryFn: () => api<{ items: FeedbackView[] }>(`/api/v1/executions/${executionId}/feedback`),
    enabled: !!executionId,
  });
  const byPath = new Map((feedback.data?.items ?? []).map((f) => [f.path, f]));
  const send = useMutation({
    mutationFn: (item: FeedbackItem) =>
      api<FeedbackResult>(`/api/v1/executions/${executionId}/feedback`, {
        method: 'POST',
        body: { items: [item] },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback', executionId] }),
  });
  const submit: Submit = (item) => send.mutate({ type: 'Fact', ...item });
  const groups: {
    key: string;
    screen: string;
    section: string;
    entries: IfsDraftShape['gbaPlan'];
  }[] = [];
  for (const e of draft.gbaPlan) {
    const key = `${e.screen}|${e.section}`;
    const g = groups.at(-1);
    if (g?.key === key) g.entries.push(e);
    else groups.push({ key, screen: e.screen, section: e.section, entries: [e] });
  }
  const download = () => {
    const blob = new Blob([toMarkdown(draft)], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ifs-draft-${draft.aad.issueKey ?? 'aad'}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const s = draft.summary;
  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center gap-3">
        <CardTitle>IFS draft (GBA entry plan)</CardTitle>
        <p className="text-xs text-muted" role="status">
          {s.completeness !== undefined && <>{s.completeness}% complete · </>}
          {s.entries} fields · {s.verified} verified · {s.inferred} inferred · {s.needsInput} need
          input · findings {s.findings.high} high / {s.findings.med} medium / {s.findings.low} low
        </p>
        <Button className="ml-auto" onClick={download}>
          <Download aria-hidden="true" className="size-4" /> Download Markdown
        </Button>
      </CardHeader>
      <CardBody className="space-y-6">
        {send.isError && (
          <p className="text-sm text-fail" role="alert">
            {send.error instanceof Error ? send.error.message : 'The review could not be saved'}
          </p>
        )}
        {canReview && (
          <p className="rounded-md border border-border px-3 py-2 text-xs text-muted">
            Accept or correct values and findings to teach the platform: they become knowledge
            candidates and, once validated in Knowledge → Review, later drafts reuse them.
          </p>
        )}
        <p className="text-sm text-muted">
          Prepared from <Mono>{draft.aad.title}</Mono>. Review every value before entering it in
          GBA; values marked Requires_confirmation or Unknown need the Integration Architect.
        </p>

        {draft.findings.length > 0 && (
          <section aria-labelledby="ifs-findings">
            <h3 id="ifs-findings" className="mb-2 text-sm font-semibold">
              Findings against the standards
            </h3>
            <ul className="space-y-2 text-sm">
              {draft.findings.map((f, i) => (
                <li key={i} className="flex flex-wrap items-start gap-2">
                  <StatusBadge
                    status={
                      f.severity === 'High' ? 'fail' : f.severity === 'Med' ? 'degraded' : 'unknown'
                    }
                    label={f.severity}
                  />
                  <span className="min-w-0 flex-1">
                    {f.message}
                    <span className="block text-xs text-muted">
                      {f.origin === 'review'
                        ? f.verified
                          ? 'Standards review · rule quoted verbatim'
                          : 'Standards review · rule not verified — check the standard'
                        : 'Documented check'}
                      {f.recommendation ? ` · Recommendation: ${f.recommendation}` : ''}
                    </span>
                    {executionId && (
                      <ReviewCell
                        path={`ifs.findings.${i}`}
                        label={`${draft.aad.title} — finding (${f.rule})`}
                        value={`${f.message}${f.recommendation ? ` Recommendation: ${f.recommendation}` : ''}`}
                        type="LessonLearned"
                        done={byPath.get(`ifs.findings.${i}`)}
                        canReview={canReview}
                        busy={send.isPending}
                        submit={submit}
                      />
                    )}
                    {f.standardTitle && (
                      <span className="block text-xs text-muted">
                        Standard: {f.standardTitle}
                        {f.standard && (
                          <>
                            {' '}
                            (<Mono>{refLabel(f.standard)}</Mono>)
                          </>
                        )}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {draft.artifacts && (
          <IfsArtifactsView
            artifacts={draft.artifacts}
            models={draft.operationModels}
            fileBase={`ifs-${draft.aad.issueKey ?? 'aad'}`}
          />
        )}

        {groups.map((g) => (
          <section key={g.key} aria-label={`${g.screen} ${g.section}`}>
            <h3 className="mb-2 text-sm font-semibold">
              {g.screen} · {g.section}
            </h3>
            <Table>
              <thead>
                <tr>
                  <Th className="w-56">Field</Th>
                  <Th>Value</Th>
                  <Th className="w-40">Status</Th>
                  <Th className="w-12">
                    <span className="sr-only">Copy</span>
                  </Th>
                  {executionId && <Th className="w-56">Review</Th>}
                </tr>
              </thead>
              <tbody>
                {g.entries.map((e) => (
                  <tr key={e.order}>
                    <Td className="align-top font-medium">{e.field}</Td>
                    <Td className="align-top">
                      {e.value.value !== null ? (
                        <span className="whitespace-pre-wrap">{e.value.value}</span>
                      ) : (
                        <span className="italic text-muted">{e.value.rationale ?? 'No value'}</span>
                      )}
                      {e.value.evidence.length > 0 && (
                        <span className="mt-1 block text-xs text-muted">
                          Evidence:{' '}
                          {e.value.evidence.map((r, i) => (
                            <Mono key={i}>{refLabel(r)}</Mono>
                          ))}
                        </span>
                      )}
                      {e.value.value !== null && e.value.rationale && (
                        <span className="mt-1 block text-xs text-muted">{e.value.rationale}</span>
                      )}
                    </Td>
                    <Td className="align-top">
                      <DomainStatus value={e.value.status.toLowerCase()} />
                    </Td>
                    <Td className="align-top">
                      {e.value.value !== null && <CopyButton text={e.value.value} />}
                    </Td>
                    {executionId && (
                      <Td className="align-top">
                        <ReviewCell
                          path={`ifs.gbaPlan.${e.order}`}
                          label={`${draft.aad.title} — ${e.screen} · ${e.section} · ${e.field}`}
                          value={e.value.value}
                          type="Fact"
                          done={byPath.get(`ifs.gbaPlan.${e.order}`)}
                          canReview={canReview}
                          busy={send.isPending}
                          submit={submit}
                        />
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </section>
        ))}

        {draft.dataModel.length > 0 && (
          <section aria-labelledby="ifs-data-model">
            <h3 id="ifs-data-model" className="mb-2 text-sm font-semibold">
              Data model (from the AAD field table)
            </h3>
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>Part</Th>
                    <Th>Action</Th>
                    <Th>Field</Th>
                    <Th>Type</Th>
                    <Th>Occ.</Th>
                    <Th>Max</Th>
                    <Th>GDPR</Th>
                    <Th>Description</Th>
                  </tr>
                </thead>
                <tbody>
                  {draft.dataModel.map((r, i) => (
                    <tr key={i}>
                      <Td>{r.part}</Td>
                      <Td>{r.action}</Td>
                      <Td>
                        <Mono>{r.field}</Mono>
                      </Td>
                      <Td>{r.type}</Td>
                      <Td>{r.occurrence}</Td>
                      <Td>{r.maxLength}</Td>
                      <Td>{r.gdpr ? 'Yes' : 'No'}</Td>
                      <Td>{r.description}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </section>
        )}

        {draft.openQuestions.length > 0 && (
          <section aria-labelledby="ifs-questions">
            <h3 id="ifs-questions" className="mb-2 text-sm font-semibold">
              Open questions for the Integration Architect
            </h3>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {draft.openQuestions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </section>
        )}
      </CardBody>
    </Card>
  );
}
